import { useCallback, useEffect, useState } from 'react'
import { mmToPx } from '../../domain/shared/units'
import { useCanvasStore } from '../../stores/canvasStore'

type SelectionBoxProps = {
  x: number
  y: number
  width: number
  height: number
  rotation: number
  rotationCenterX?: number // Optional rotation center
  rotationCenterY?: number // Optional rotation center
  onRotate: (angle: number) => void
  onResize: (width: number, height: number) => void
  scale: number
  elementId: string
}

export const SelectionBox = ({
  x,
  y,
  width,
  height,
  rotation,
  rotationCenterX,
  rotationCenterY,
  onRotate,
  onResize,
  scale,
  elementId,
}: SelectionBoxProps) => {
  const [isRotating, setIsRotating] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [lastAngle, setLastAngle] = useState(0)
  const [currentRotation, setCurrentRotation] = useState(0)
  const [lockedCenterScreen, setLockedCenterScreen] = useState({ x: 0, y: 0 })
  const [startMousePos, setStartMousePos] = useState({ x: 0, y: 0 })
  const [startSize, setStartSize] = useState({ width: 0, height: 0 })

  const startDrag = useCanvasStore((state) => state.startDrag)
  const endDrag = useCanvasStore((state) => state.endDrag)

  // Calculate center point for the box
  const boxCenterX = x + width / 2
  const boxCenterY = y + height / 2

  // Use provided rotation center or fall back to box center
  const rotCenterX = rotationCenterX ?? boxCenterX
  const rotCenterY = rotationCenterY ?? boxCenterY

  // Handle size (scaled for visibility)
  const handleSize = 10 / scale

  // Calculate angle from a fixed center point to mouse position
  const calculateAngleFromCenter = useCallback(
    (clientX: number, clientY: number, centerX: number, centerY: number) => {
      return Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI)
    },
    [],
  )

  // Handle rotation start
  const handleRotateStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation()
      const rect = (e.target as Element).closest('svg')?.getBoundingClientRect()
      if (!rect) return

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

      // Lock the rotation center in screen coordinates
      const centerScreenX = rect.left + rotCenterX * scale
      const centerScreenY = rect.top + rotCenterY * scale

      const mouseDownAngle = calculateAngleFromCenter(
        clientX,
        clientY,
        centerScreenX,
        centerScreenY,
      )

      setIsRotating(true)
      setLastAngle(mouseDownAngle)
      setCurrentRotation(rotation)
      setLockedCenterScreen({ x: centerScreenX, y: centerScreenY })
      startDrag(elementId) // Start drag to pause temporal history
    },
    [rotation, calculateAngleFromCenter, elementId, startDrag, scale, rotCenterX, rotCenterY],
  )

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation()
      e.preventDefault()

      setIsResizing(true)
      // Store initial client coordinates for relative tracking
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
      setStartMousePos({ x: clientX, y: clientY })
      setStartSize({ width, height })
      startDrag(elementId) // Start drag to pause temporal history
    },
    [width, height, elementId, startDrag],
  )

  // Handle mouse/touch move
  useEffect(() => {
    if (!isRotating && !isResizing) return

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

      if (isRotating) {
        // Use the locked center that was captured at mousedown
        const newAngle = calculateAngleFromCenter(
          clientX,
          clientY,
          lockedCenterScreen.x,
          lockedCenterScreen.y,
        )

        // Calculate delta from last position (not from start!)
        let deltaAngle = newAngle - lastAngle

        // Handle wraparound: if delta > 180, we crossed the boundary going backwards
        if (deltaAngle > 180) {
          deltaAngle -= 360
        } else if (deltaAngle < -180) {
          deltaAngle += 360
        }

        const newRotation = currentRotation + deltaAngle

        // Update last angle and current rotation for next move
        setLastAngle(newAngle)
        setCurrentRotation(newRotation)

        // Normalize to -180 to 180
        let normalizedRotation = ((newRotation + 180) % 360) - 180

        // Snap to horizontal (0 degrees) if within 5 degrees
        const snapThreshold = 5
        if (Math.abs(normalizedRotation) <= snapThreshold) {
          normalizedRotation = 0
        } else if (
          Math.abs(normalizedRotation - 180) <= snapThreshold ||
          Math.abs(normalizedRotation + 180) <= snapThreshold
        ) {
          // Also snap to 180/-180 degrees (upside down)
          normalizedRotation = 180
        }

        onRotate(normalizedRotation)
      } else if (isResizing) {
        // Calculate delta in client space (screen pixels)
        const deltaX = clientX - startMousePos.x
        const deltaY = clientY - startMousePos.y

        // Average the deltas for diagonal resizing
        const avgDelta = (deltaX + deltaY) / 2

        // Scale the delta by the current zoom to get SVG units
        const scaledDelta = avgDelta / scale

        // Calculate aspect ratio from the start size
        const aspectRatio = startSize.width / startSize.height

        // Apply the delta to width and calculate height based on aspect ratio
        const newWidth = startSize.width + scaledDelta

        // Ensure minimum size (4mm minimum)
        const minSize = mmToPx(4)

        // Apply minimum size constraint to width first
        const constrainedWidth = Math.max(minSize, newWidth)

        // Calculate height based on aspect ratio to preserve it
        const constrainedHeight = constrainedWidth / aspectRatio

        onResize(constrainedWidth, constrainedHeight)
      }
    }

    const handleEnd = () => {
      setIsRotating(false)
      setIsResizing(false)
      endDrag() // Resume temporal history and trigger auto-save
    }

    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleEnd)
    document.addEventListener('touchmove', handleMove)
    document.addEventListener('touchend', handleEnd)

    return () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleEnd)
      document.removeEventListener('touchmove', handleMove)
      document.removeEventListener('touchend', handleEnd)
    }
  }, [
    isRotating,
    isResizing,
    lastAngle,
    currentRotation,
    lockedCenterScreen,
    startMousePos,
    startSize,
    scale,
    calculateAngleFromCenter,
    onRotate,
    onResize,
    endDrag,
  ])

  return (
    <g
      transform={`rotate(${rotation} ${rotCenterX} ${rotCenterY})`}
      data-selection-box
      data-gtm="selection-box"
    >
      {/* Selection box */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="none"
        stroke="#3b82f6"
        strokeWidth={2 / scale}
        strokeDasharray={`${5 / scale} ${5 / scale}`}
        pointerEvents="none"
      />

      {/* Rotation handle - top center */}
      <line
        x1={boxCenterX}
        y1={y}
        x2={boxCenterX}
        y2={y - 25 / scale}
        stroke="#3b82f6"
        strokeWidth={2 / scale}
        pointerEvents="none"
      />
      <circle
        cx={boxCenterX}
        cy={y - 25 / scale}
        r={handleSize}
        fill="#3b82f6"
        stroke="white"
        strokeWidth={2 / scale}
        className="cursor-pointer"
        onMouseDown={handleRotateStart}
        onTouchStart={handleRotateStart}
      />

      {/* Resize handle - bottom right corner */}
      <circle
        cx={x + width}
        cy={y + height}
        r={handleSize}
        fill="white"
        stroke="#3b82f6"
        strokeWidth={2 / scale}
        className="cursor-se-resize"
        data-handle-type="resize"
        onMouseDown={handleResizeStart}
        onTouchStart={handleResizeStart}
      />
    </g>
  )
}
